import { Link } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  Trophy,
  Briefcase,
  MapPin,
  Plane,
  Sparkles,
} from "lucide-react";
import Seo from "@/components/Seo";
import { DESTINATION_IMAGES } from "@/lib/media";

const BREADCRUMB = [
  { name: "Home", path: "/" },
  { name: "Cambodia DMC", path: "/cambodia-dmc" },
];

const SERVICES = [
  {
    icon: Trophy,
    title: "MICE",
    copy:
      "Cambodia is rapidly establishing itself as a compelling MICE destination in Southeast Asia, offering unique venue experiences that no ballroom can replicate. Trawelldmc handles end-to-end MICE logistics across Siem Reap and Phnom Penh — from airport transfers and hotel contracting to themed gala dinners within Angkor Archaeological Park permits, conference setups, and post-event leisure extensions. Our Cambodia MICE programmes are ideal for groups of 20 to 500, with strong hotel partnerships across Sokha, Raffles, and Rosewood properties. We manage visas on arrival coordination, group manifests, and dedicated event managers on ground.",
  },
  {
    icon: Briefcase,
    title: "Group Leisure",
    copy:
      "Cambodia's cultural richness makes it a standout for group leisure travel. We design fully escorted group programmes covering the iconic Angkor Wat temple complex, the historic Killing Fields and Royal Palace in Phnom Penh, Tonle Sap floating villages, and the pristine beaches of Koh Rong. Our group departures are available as fixed departures or customised private groups, with English and Hindi-speaking guides, comfortable air-conditioned coaches, and hand-picked mid-range to luxury hotels. Ideal for alumni groups, associations, religious tours, and corporate leisure.",
  },
  {
    icon: Sparkles,
    title: "FIT",
    copy:
      "For the independent traveller seeking depth over speed, Cambodia rewards slow exploration. Our FIT desk offers fully customised Cambodia itineraries — from 4-night Siem Reap temple-focus programmes to 8-night multi-city journeys combining Phnom Penh, Siem Reap, and a Mekong river extension into Vietnam. All FIT packages include private transfers, expert local guides, pre-booked hotel accommodation, and 24/7 on-ground support. We cater to honeymooners, heritage travellers, photography enthusiasts, and solo explorers.",
  },
];

const HIGHLIGHTS = [
  "Angkor Wat sunrise tours",
  "Phnom Penh city experiences",
  "Siem Reap gala venues",
  "Mekong river cruises",
  "Cambodian cooking classes",
  "Artisan village visits",
];

const VENUES = [
  { name: "Sokha Siem Reap Resort", note: "5-star MICE infrastructure adjacent to Angkor" },
  { name: "Raffles Grand Hotel d'Angkor", note: "Heritage property — iconic gala venue" },
  { name: "Rosewood Phnom Penh", note: "Capital's tallest building, modern MICE floors" },
  { name: "Angkor Archaeological Park", note: "Permit-only private gala settings" },
  { name: "Sofitel Phnom Penh Phokeethra", note: "Boardrooms, ballrooms, riverside grounds" },
  { name: "Le Méridien Angkor", note: "Mid-large group accommodation in Siem Reap" },
];

const FAQS = [
  {
    q: "What group sizes can Trawelldmc handle in Cambodia?",
    a: "We operate programmes from 20-pax executive retreats up to 500-pax incentive groups. Siem Reap holds the larger hotel inventory; Phnom Penh adds capital-city venues. For 500+ pax, we split inventory across both cities or stage events at exclusive Angkor Archaeological Park permits.",
  },
  {
    q: "Do you arrange Cambodia visas for groups?",
    a: "Yes. Cambodia offers visa-on-arrival for most passports plus an e-Visa option. We coordinate group manifests, manage documentation, and pre-clear large arrivals with airport authorities to ensure 200+ pax move from arrival to transfer in under 45 minutes.",
  },
  {
    q: "What is the best time of year to visit Cambodia?",
    a: "November to February delivers cool, dry weather — ideal for outdoor temple visits and gala dinners. March to May is hot but very photogenic. June to October brings green-season rains; afternoons can be wet but mornings remain workable for temple programmes.",
  },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function CambodiaDmc() {
  return (
    <div data-testid="cambodia-dmc-page">
      <Seo
        path="/cambodia-dmc"
        title="Asia DMC Cambodia | MICE & Group Tours | Trawelldmc"
        description="DMC in Cambodia — Siem Reap, Phnom Penh & Mekong programmes. MICE, group leisure & FIT travel built end-to-end. Request a Cambodia proposal."
        breadcrumb={BREADCRUMB}
        extraJsonLd={FAQ_JSONLD}
      />

      <section className="relative">
        <img
          src={DESTINATION_IMAGES.cambodia}
          alt="Angkor Wat sunrise — Trawelldmc DMC Cambodia"
          width="1920"
          height="1080"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(11,25,44,0.92) 0%, rgba(11,25,44,0.55) 100%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-32 md:py-44 text-white">
          <div className="max-w-3xl">
            <div className="overline !text-[#FF5238] mb-5">
              Cambodia DMC · Siem Reap · Phnom Penh
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.06]">
              <span className="text-[#FF5238]">Cambodia</span> Destination Management Company (DMC) & MICE Specialist
            </h1>
            <p className="text-white/80 mt-6 text-sm tracking-[0.2em] uppercase font-medium">
              Ancient Temples, Timeless Journeys
            </p>
            <p className="text-white/80 mt-6 text-lg max-w-2xl leading-relaxed">
              MICE, group leisure and FIT programmes across Cambodia's
              UNESCO-listed temple country — engineered end-to-end by our
              on-ground team in Siem Reap and Phnom Penh.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/inquiry?destination=Cambodia"
                className="btn-primary"
                data-testid="kh-cta-proposal"
              >
                Get a Cambodia proposal <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/quote" className="btn-secondary" data-testid="kh-cta-ai-quote">
                Instant AI estimate
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CAMBODIA / OVERVIEW */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline mb-4">Why Cambodia</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Depth, drama, <br />and exceptional value.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-slate-700 text-lg leading-relaxed">
            <p>
              Cambodia is a destination of extraordinary depth — where ancient
              civilisations meet emerging modern infrastructure. As your DMC
              partner, Trawelldmc curates seamless group and MICE experiences
              across Siem Reap, Phnom Penh, and the coastal gem of Sihanoukville.
              From exclusive Angkor Wat sunrise privates to riverside gala
              dinners and culturally immersive incentive programmes, Cambodia
              delivers high-impact experiences at exceptional value.
            </p>
            <p>
              Our on-ground team manages every detail — visas, transfers,
              venues, and guides — ensuring flawless execution for groups of
              all sizes. Few destinations let you stage a 200-pax gala dinner
              beneath the silhouette of a thousand-year-old temple. Cambodia
              does.
            </p>
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline mb-4">Top Experiences</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Cambodia highlights.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {HIGHLIGHTS.map((h) => (
              <div key={h} className="card-flat p-6 flex items-start gap-3">
                <MapPin className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-0.5" />
                <span className="text-[#0B192C] font-medium">{h}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="mb-16 max-w-2xl">
          <div className="overline mb-4">What we deliver</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
            Our Cambodia services.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {SERVICES.map((s) => (
            <div key={s.title} className="card-flat p-8" data-testid={`kh-service-${s.title.toLowerCase().replace(/ /g, "-")}`}>
              <s.icon className="h-8 w-8 text-[#FF5238]" />
              <h3 className="font-heading text-xl font-medium tracking-tight text-[#0B192C] mt-5">
                {s.title}
              </h3>
              <p className="text-slate-600 mt-3 leading-relaxed text-sm">{s.copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* VENUES */}
      <section className="bg-[#0B192C] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="mb-16 max-w-2xl">
            <div className="overline !text-[#FF5238] mb-4">Top venues</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight">
              Hotels & venues <br />
              <span className="text-[#FF5238]">we partner with.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
            {VENUES.map((v) => (
              <div key={v.name} className="border-t border-white/15 pt-5">
                <div className="flex gap-3 items-start">
                  <Building2 className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-heading text-lg font-medium">{v.name}</div>
                    <div className="text-sm text-white/60 mt-1">{v.note}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
        <div className="mb-16 max-w-2xl">
          <div className="overline mb-4">FAQ</div>
          <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
            Frequently Asked Questions
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
          {FAQS.map((f, i) => (
            <div key={f.q} data-testid={`kh-faq-${i}`}>
              <h3 className="font-heading text-xl font-medium text-[#0B192C] tracking-tight">
                {f.q}
              </h3>
              <p className="text-slate-600 mt-3 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#FDFCF8] border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="overline mb-4">Next step</div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#0B192C]">
              Ready to plan your <br />
              <span className="text-[#FF5238]">Cambodia programme?</span>
            </h2>
            <p className="text-slate-600 mt-6 max-w-xl text-lg leading-relaxed">
              Contact Trawelldmc for a custom Cambodia proposal. We respond
              within 24 hours.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-4">
            <Link
              to="/inquiry?destination=Cambodia"
              className="btn-primary w-full justify-center"
              data-testid="kh-bottom-cta"
            >
              Request a proposal <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" className="btn-secondary w-full justify-center">
              Contact our team
            </Link>
          </div>
        </div>
      </section>

      {/* YOU MAY ALSO LIKE */}
      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-16">
          <div className="overline mb-6">You may also like</div>
          <nav
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            aria-label="Related DMC destination pages"
          >
            <Link to="/vietnam-dmc" className="group flex items-start gap-3" data-testid="kh-link-vietnam">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Vietnam</div>
                <div className="text-sm text-slate-500 mt-1">Hanoi · HCMC · Ha Long</div>
              </div>
            </Link>
            <Link to="/laos-dmc" className="group flex items-start gap-3" data-testid="kh-link-laos">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Laos</div>
                <div className="text-sm text-slate-500 mt-1">Luang Prabang · Vientiane</div>
              </div>
            </Link>
            <Link to="/thailand-dmc" className="group flex items-start gap-3" data-testid="kh-link-thailand">
              <MapPin className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">DMC Thailand</div>
                <div className="text-sm text-slate-500 mt-1">Bangkok · Phuket · Chiang Mai</div>
              </div>
            </Link>
            <Link to="/" className="group flex items-start gap-3" data-testid="kh-link-home">
              <Plane className="h-5 w-5 text-[#FF5238] mt-0.5" />
              <div>
                <div className="font-medium text-[#0B192C] group-hover:underline">Trawelldmc Home</div>
                <div className="text-sm text-slate-500 mt-1">All destinations & services</div>
              </div>
            </Link>
          </nav>
        </div>
      </section>
    </div>
  );
}
